/**
 * The bar shown while a platform admin is inside a firm's app on a support
 * session (Phase 26). It says whose data this is, that nothing can be
 * changed, and when access ends — and offers to end it now.
 *
 * Read-only is enforced by the database (0033's SELECT-only rules), not by
 * this bar: an edit attempted here is simply refused.
 */

import { esc } from '@/lib/dom';
import { endSupportSession, type SupportSession } from '@/lib/data/admin';
import { minutesLeft, sessionEndsText } from '@/lib/admin';
import { toastError } from '@/lib/toast';

/** How often the "ends in" text refreshes. Stops when the page reloads. */
const TICK_MS = 30_000;

export function mountSupportBanner(root: HTMLElement, session: SupportSession): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'support-banner';
  bar.setAttribute('role', 'status');
  bar.innerHTML = `
    <span><strong>Support mode</strong> — viewing ${esc(session.firm_name)}, read-only.
      Changes won’t be saved. <span data-ends>${esc(sessionEndsText(session.expires_at))}</span></span>
    <button class="btn-sm" data-action="end-support">End session</button>`;
  root.append(bar);

  const back = (): void => { location.hash = ''; location.reload(); };

  // Bounded: refreshes the text; once the session has expired the database
  // already shows nothing, so return to the admin page instead of ticking on.
  const timer = window.setInterval(() => {
    const ends = bar.querySelector<HTMLElement>('[data-ends]');
    if (ends) ends.textContent = sessionEndsText(session.expires_at);
    if (minutesLeft(session.expires_at) === 0) { window.clearInterval(timer); back(); }
  }, TICK_MS);

  bar.querySelectorAll<HTMLButtonElement>('[data-action="end-support"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        await endSupportSession();
        window.clearInterval(timer);
        back();
      } catch (err) {
        btn.disabled = false;
        toastError(err instanceof Error ? err.message : 'Could not end the session.');
      }
    });
  });
  return bar;
}
